'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback, useMemo } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { UserProfile } from '@/types/database';
import { useRefetchOnReconnect } from '@/hooks/useRefetchOnReconnect';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  isAdmin: boolean;
  isTechnician: boolean;
  isClient: boolean;
  clientCampaignId: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Create client once and keep it stable across renders
  const [supabase] = useState(() => createClient());

  // Guard against concurrent initialization (React Strict Mode causes double-mount)
  const initializingRef = useRef(false);
  const initializedRef = useRef(false);

  // Track last fetched user to prevent duplicate fetches
  const lastFetchedUserIdRef = useRef<string | null>(null);
  const pendingFetchRef = useRef<Promise<UserProfile | null> | null>(null);
  // Track which userId the in-flight fetch belongs to (set on start, cleared on finish)
  const pendingUserIdRef = useRef<string | null>(null);

  // Fetch user profile from database with deduplication
  const fetchUserProfile = useCallback(async (userId: string, force: boolean = false): Promise<UserProfile | null> => {
    // Skip if already fetched for this user (unless forced)
    if (!force && lastFetchedUserIdRef.current === userId) {
      console.log('[AuthContext] Skipping duplicate fetch for user:', userId);
      return userProfile;
    }

    // Return pending fetch if one exists for the same user
    if (pendingFetchRef.current && pendingUserIdRef.current === userId) {
      console.log('[AuthContext] Returning pending fetch for user:', userId);
      return pendingFetchRef.current;
    }

    console.log('[AuthContext] Fetching profile for user:', userId);

    const fetchPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error) {
          // PGRST116 = "no rows returned" - this is expected for new users without profiles
          if (error.code === 'PGRST116') {
            console.log('[AuthContext] No user profile found for user:', userId);
          } else {
            console.error('[AuthContext] Error fetching user profile:', error.message || error);
          }
          return null;
        }
        console.log('[AuthContext] Profile loaded:', { role: data?.role, email: data?.email });
        lastFetchedUserIdRef.current = userId;
        return data as UserProfile;
      } catch (err) {
        // Handle AbortError gracefully
        if (err instanceof Error && (err.name === 'AbortError' || err.message?.includes('aborted'))) {
          console.debug('[AuthContext] Profile fetch aborted');
          return null;
        }
        console.error('[AuthContext] Error fetching profile:', err);
        return null;
      } finally {
        pendingFetchRef.current = null;
        pendingUserIdRef.current = null;
      }
    })();

    pendingUserIdRef.current = userId;
    pendingFetchRef.current = fetchPromise;
    return fetchPromise;
  }, [supabase, userProfile]);

  useEffect(() => {
    // Prevent concurrent initialization from React Strict Mode double-mount
    if (initializingRef.current || initializedRef.current) {
      return;
    }
    initializingRef.current = true;

    let mounted = true;

    // Get initial session
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        // Handle AbortError gracefully
        if (error) {
          if (error.message?.includes('aborted') || error.name === 'AbortError') {
            console.debug('[AuthContext] getSession aborted');
            return;
          }
          console.error('[AuthContext] getSession error:', error);
        }

        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Force fetch on initial load
          const profile = await fetchUserProfile(session.user.id, true);
          if (mounted) {
            setUserProfile(profile);
          }
        }

        if (mounted) {
          setIsLoading(false);
          initializedRef.current = true;
          console.log('[AuthContext] Initialization complete');
        }
      } catch (err) {
        // Handle AbortError gracefully
        if (err instanceof Error && (err.name === 'AbortError' || err.message?.includes('aborted'))) {
          console.debug('[AuthContext] Auth init aborted');
          return;
        }
        console.error('[AuthContext] Auth init error:', err);
        if (mounted) {
          setIsLoading(false);
        }
      } finally {
        initializingRef.current = false;
      }
    };

    initAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session) => {
      if (!mounted) return;

      console.log('[AuthContext] Auth state change:', event);

      // Only process meaningful auth events
      // Skip INITIAL_SESSION as we handle that in initAuth
      // Skip TOKEN_REFRESHED as the user hasn't changed
      if (event === 'INITIAL_SESSION') {
        console.log('[AuthContext] Skipping INITIAL_SESSION (handled by initAuth)');
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        console.log('[AuthContext] Skipping TOKEN_REFRESHED (same user)');
        // Still update session object for fresh tokens
        setSession(session);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const userId = session.user.id;
        const forceRefresh = event === 'SIGNED_IN' || event === 'USER_UPDATED';

        // supabase-js fires SIGNED_IN / USER_UPDATED while holding the internal auth
        // lock (lockAcquired = true). Calling supabase.from() here triggers an internal
        // getSession() call which tries to re-acquire the same lock. Because pendingInLock
        // only drains after the outer lock's callback returns — and that callback is
        // waiting for us — this creates a circular deadlock and the spinner never clears.
        //
        // Deferring to a macrotask (setTimeout 0) lets the auth lock release first.
        // initAuth's getSession() returns, initAuth fetches the profile on the fast path,
        // and the deferred call here either reuses the pending promise or is a no-op.
        setTimeout(async () => {
          if (!mounted) return;
          const profile = await fetchUserProfile(userId, forceRefresh);
          if (mounted) {
            setUserProfile(profile);
            setIsLoading(false);
          }
        }, 0);
        return; // setIsLoading is handled inside the setTimeout above
      } else {
        // User signed out - clear profile and reset lastFetchedUserId
        setUserProfile(null);
        lastFetchedUserIdRef.current = null;
      }

      setIsLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the profile fetch failed while offline or while the tab was
  // backgrounded (network drop mid-fetch, browser throttling a hidden tab),
  // there's otherwise no retry — re-fetch once connectivity/visibility is
  // restored, same as Booking/Campaign contexts.
  useRefetchOnReconnect(() => {
    if (user) {
      fetchUserProfile(user.id, true).then((profile) => setUserProfile(profile));
    }
  });

  // Derived role properties
  const isAdmin = userProfile?.role === 'admin';
  const isTechnician = userProfile?.role === 'technician';
  const isClient = userProfile?.role === 'client';
  const clientCampaignId = userProfile?.client_campaign_id || null;

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  }, [supabase]);

  const signOut = useCallback(async () => {
    console.log('[AuthContext] signOut called');

    // Clear any pending fetch operations
    pendingFetchRef.current = null;
    lastFetchedUserIdRef.current = null;

    // Clear local state immediately
    setUser(null);
    setSession(null);
    setUserProfile(null);

    // Clear Supabase auth storage directly (don't wait for signOut which can hang)
    try {
      // Clear localStorage items used by Supabase auth
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('[AuthContext] Cleared Supabase localStorage keys:', keysToRemove.length);
    } catch (err) {
      console.error('[AuthContext] Error clearing localStorage:', err);
    }

    // Fire and forget - don't wait for this
    supabase.auth.signOut().catch(err => {
      console.log('[AuthContext] signOut background call failed (expected):', err);
    });

    console.log('[AuthContext] signOut completed (state cleared)');
  }, [supabase]);

  const value = useMemo(() => ({
    user,
    session,
    userProfile,
    isAdmin,
    isTechnician,
    isClient,
    clientCampaignId,
    isLoading,
    signIn,
    signOut,
  }), [user, session, userProfile, isAdmin, isTechnician, isClient, clientCampaignId, isLoading, signIn, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
